"use client";
// ===================================================
// מחברת — the active note: title, details, body, save state.
// Mounted with key={note.id}: one autosave controller per note, so text typed
// here can only ever be written to this note's row.
// Spec: docs/specs/pinkas-unified-workspace-20260924.md §4
// ===================================================
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Editor, JSONContent } from "@tiptap/core";
import { AlertCircle, Check, ChevronDown, Layers, Loader2, Pin, RotateCw, X } from "lucide-react";
import { Button, Input } from "@/components/ui";
import { TiptapEditor } from "@/components/editor/TiptapEditor";
import type { PinkasItem, PinkasPatch, PinkasPriority } from "@/lib/cc-pinkas-queries";
import { useNoteAutosave, type SaveNoteFn } from "@/lib/pinkas/useNoteAutosave";
import { UNTITLED, type NotePosition } from "@/lib/pinkas/workspace";
import { ASSIGNEES, KINDS, KIND_META, NOTE_COLORS, PRIORITIES, STATUSES } from "@/lib/pinkas/vocab";
import { IconAction, Seg } from "./PinkasControls";

export function PinkasNoteEditor({
  note,
  draftKey,
  save,
  onPatch,
  inTray,
  onToggleTray,
  detailsOpen,
  onToggleDetails,
  position,
  onPosition,
  compact = false,
}: {
  note: PinkasItem;
  draftKey: string;
  save: SaveNoteFn;
  /** Details (kind, colour, tags…) save at once through the list's patch path. */
  onPatch: (patch: PinkasPatch) => void;
  inTray: boolean;
  onToggleTray: () => void;
  detailsOpen: boolean;
  onToggleDetails: () => void;
  position: NotePosition | undefined;
  onPosition: (pos: NotePosition) => void;
  /** Narrow layout: controls drop their text labels so the title keeps its room. */
  compact?: boolean;
}) {
  const server = useMemo(
    () => ({ title: note.text, body: (note.body as JSONContent | null) ?? null, updatedAt: note.updated_at }),
    [note.text, note.body, note.updated_at],
  );
  const a = useNoteAutosave({ noteId: note.id, draftKey, server, save });

  // ---- cursor + scroll: restore on open, remember on leave ---------------
  const scroller = useRef<HTMLDivElement>(null);
  const editorRef = useRef<Editor | null>(null);
  const initialPos = useRef(position);
  const [focusOnOpen] = useState(!position);
  const onPositionRef = useRef(onPosition);
  onPositionRef.current = onPosition;

  const onEditorReady = useCallback((editor: Editor | null) => {
    editorRef.current = editor;
    const pos = initialPos.current;
    if (!editor || !pos) return;
    initialPos.current = undefined; // only the first mount restores
    requestAnimationFrame(() => {
      try {
        const max = editor.state.doc.content.size;
        editor.commands.setTextSelection({ from: Math.min(pos.from, max), to: Math.min(pos.to, max) });
      } catch {
        /* the doc changed shape since — the start is fine */
      }
      if (scroller.current) scroller.current.scrollTop = pos.top;
    });
  }, []);

  useEffect(() => {
    const el = scroller.current;
    return () => {
      const ed = editorRef.current;
      const sel = ed && !ed.isDestroyed ? ed.state.selection : null;
      onPositionRef.current({ top: el?.scrollTop ?? 0, from: sel?.from ?? 1, to: sel?.to ?? 1 });
    };
  }, []);

  const onKeyDownSave = (e: React.KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
      e.preventDefault();
      void a.flush();
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col" onKeyDown={onKeyDownSave}>
      {/* title row ------------------------------------------------------------ */}
      <div className={`flex items-center border-b border-[var(--border-hairline)] py-2 ${compact ? "gap-1 px-2" : "gap-2 px-3"}`}>
        <span aria-hidden className="text-lg leading-none" title={KIND_META[note.kind].label}>
          {KIND_META[note.kind].emoji}
        </span>
        <div className="min-w-0 flex-1">
          <Input
            aria-label="כותרת הפתק"
            value={a.title}
            onChange={(e) => a.setTitle(e.target.value)}
            placeholder={UNTITLED}
            className="font-semibold"
            style={{ height: 34, fontSize: 16, background: "transparent", borderColor: "transparent" }}
          />
        </div>
        <SaveState status={a.status} error={a.errorMessage} onRetry={() => void a.flush()} compact={compact} />
        <IconAction icon={Layers} label={inTray ? "הוצא מהמגש" : "השאר במגש"} active={inTray} onClick={onToggleTray} />
        <IconAction icon={Pin} label={note.pinned ? "בטל נעיצה" : "נעץ"} active={note.pinned} onClick={() => onPatch({ pinned: !note.pinned })} />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          iconRight={ChevronDown}
          aria-label="פרטים"
          aria-expanded={detailsOpen}
          aria-controls={`pinkas-details-${note.id}`}
          onClick={onToggleDetails}
          className="text-[var(--theme-text-muted)]"
          style={{ height: 28, paddingInline: compact ? 6 : 8, borderRadius: 6, flexShrink: 0 }}
        >
          {compact ? null : "פרטים"}
        </Button>
      </div>

      {/* notices: recovered draft / conflict -------------------------------- */}
      {a.status === "conflict" ? (
        <div role="alert" className="flex flex-wrap items-center gap-2 border-b border-[var(--border-hairline)] bg-[var(--theme-surface-2)] px-3 py-2 text-xs text-[var(--theme-text)]">
          <AlertCircle size={14} className="text-[var(--role-warning)]" aria-hidden />
          <span className="flex-1">נמצאה טיוטה שלא נשמרה, והפתק השתנה מאז גם בשרת. איזו גרסה להשאיר?</span>
          <Button size="sm" type="button" onClick={() => a.resolveConflict("local", server)}>
            את הטיוטה שלי
          </Button>
          <Button size="sm" type="button" variant="secondary" onClick={() => a.resolveConflict("server", server)}>
            את הגרסה מהשרת
          </Button>
        </div>
      ) : a.restoredDraft ? (
        <div role="status" className="flex items-center gap-2 border-b border-[var(--border-hairline)] bg-[var(--theme-surface-2)] px-3 py-1.5 text-xs text-[var(--theme-text-muted)]">
          <span className="flex-1">שוחזרה טיוטה שלא נשמרה בפעם הקודמת — היא נשמרת עכשיו.</span>
          <IconAction icon={X} label="סגור הודעה" onClick={a.dismissRestoredNotice} />
        </div>
      ) : null}

      {/* details (collapsible) ------------------------------------------------ */}
      {detailsOpen ? <NoteDetails id={`pinkas-details-${note.id}`} note={note} onPatch={onPatch} /> : null}

      {/* body ------------------------------------------------------------------ */}
      {/* The editor's block handle hangs ~42px past the text on the left: wide
          layouts leave it a gutter; narrow (touch) layouts clip it. */}
      <div
        ref={scroller}
        className={`min-h-0 flex-1 overflow-y-auto py-2 ${compact ? "overflow-x-hidden px-2" : "ps-2 pe-12"}`}
      >
        <TiptapEditor
          key={a.editorKey}
          content={a.editorContent ?? undefined}
          onChange={a.setBody}
          onSave={() => void a.flush()}
          onEditorReady={onEditorReady}
          placeholder="כתוב כאן — הוראות, שלבים, קישורים…"
          autoFocus={focusOnOpen}
        />
      </div>
    </div>
  );
}

function SaveState({ status, error, onRetry, compact }: { status: string; error: string; onRetry: () => void; compact: boolean }) {
  if (status === "error") {
    return (
      <span role="alert" className="flex shrink-0 items-center gap-1 text-[11px] text-[var(--role-error)]" title={error}>
        <AlertCircle size={13} aria-hidden />
        השמירה נכשלה
        <Button
          type="button"
          size="sm"
          variant="ghost"
          icon={RotateCw}
          aria-label="נסה שוב"
          onClick={onRetry}
          style={{ height: 22, paddingInline: 6, borderRadius: 5 }}
        >
          {compact ? null : "נסה שוב"}
        </Button>
      </span>
    );
  }
  const text = status === "saving" ? "שומר…" : status === "dirty" ? "שינויים לא שמורים" : status === "saved" ? "נשמר" : "";
  return (
    <span
      aria-live="polite"
      title={compact ? text : undefined}
      className={`flex shrink-0 items-center gap-1 text-[11px] text-[var(--theme-text-faint)] ${compact ? "" : "min-w-[60px]"}`}
    >
      {status === "saving" ? <Loader2 size={12} className="animate-spin" aria-hidden /> : null}
      {status === "saved" ? <Check size={12} className="text-[var(--role-success)]" aria-hidden /> : null}
      {status === "dirty" && compact ? <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-[var(--role-warning)]" /> : null}
      <span className={compact ? "sr-only" : undefined}>{text}</span>
    </span>
  );
}

function NoteDetails({ id, note, onPatch }: { id: string; note: PinkasItem; onPatch: (p: PinkasPatch) => void }) {
  const [tagInput, setTagInput] = useState("");
  const tags = note.tags ?? [];
  const addTag = () => {
    const t = tagInput.trim().replace(/^#/, "");
    if (t && !tags.includes(t)) onPatch({ tags: [...tags, t] });
    setTagInput("");
  };
  return (
    <div id={id} className="space-y-2 border-b border-[var(--border-hairline)] bg-[var(--theme-surface)] px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] text-[var(--theme-text-muted)]">
        <Seg label="סוג" value={note.kind} onChange={(v) => onPatch({ kind: v })} options={KINDS} />
        <Seg label="בשביל" value={note.assignee} onChange={(v) => onPatch({ assignee: v })} options={ASSIGNEES} />
        <Seg label="סטטוס" value={note.status} onChange={(v) => onPatch({ status: v })} options={STATUSES} />
        <Seg
          label="עדיפות"
          value={(note.priority ?? "none") as PinkasPriority | "none"}
          onChange={(v) => onPatch({ priority: v === "none" ? null : v })}
          options={PRIORITIES}
        />
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] text-[var(--theme-text-muted)]">
        <span className="flex items-center gap-1.5">
          יעד
          <Input
            type="date"
            inputSize="sm"
            aria-label="תאריך יעד"
            value={note.due_date ?? ""}
            onChange={(e) => onPatch({ due_date: e.target.value || null })}
            style={{ height: 26, width: 140 }}
          />
        </span>
        <span role="group" aria-label="צבע" className="flex items-center gap-1">
          צבע
          {NOTE_COLORS.map((c) => (
            <Button
              key={String(c.v)}
              type="button"
              variant="ghost"
              size="sm"
              aria-label={c.label}
              aria-pressed={note.color === c.v}
              title={c.label}
              onClick={() => onPatch({ color: c.v })}
              style={{
                width: 20,
                height: 20,
                padding: 0,
                borderRadius: 999,
                background: c.v ? c.hex : "var(--theme-surface-2)",
                border: `2px solid ${note.color === c.v ? "var(--theme-text)" : "var(--theme-border)"}`,
              }}
            >
              {c.v === null ? <span aria-hidden className="text-[9px]">∅</span> : null}
            </Button>
          ))}
        </span>
        <span className="text-[var(--theme-text-faint)]">#{note.ref}</span>
        {note.related_task_id ? <span className="text-[var(--theme-text-faint)]">🔗 מקושר למשימה</span> : null}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-[var(--theme-text-muted)]">
        תגיות
        {tags.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-0.5 rounded-md border border-[var(--theme-border)] bg-[var(--theme-surface-2)] ps-1.5 text-[var(--theme-text)]"
          >
            #{t}
            <IconAction icon={X} label={`הסר תגית ${t}`} onClick={() => onPatch({ tags: tags.filter((x) => x !== t) })} />
          </span>
        ))}
        <div className="w-28">
          <Input
            inputSize="sm"
            aria-label="הוסף תגית"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                e.preventDefault();
                addTag();
              }
            }}
            onBlur={() => tagInput.trim() && addTag()}
            placeholder="+ תגית"
            style={{ height: 24 }}
          />
        </div>
      </div>
    </div>
  );
}
